import React, { useState } from 'react';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { HostStudent } from '../../models/HostStudent';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';

export interface HostStudentRosterCardProps {
  student: HostStudent;
  onEdit: (student: HostStudent) => void;
  onReport: (student: HostStudent) => void;
  onUnblock: (student: HostStudent) => void;
  onRemove: (student: HostStudent) => void;
  canReport: boolean;
  canUnblock: boolean;
}

function schoolProfileLabel(student: HostStudent): string | null {
  const first = student.studentProfile.firstName?.trim() ?? '';
  const last = student.studentProfile.lastName?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  return combined || null;
}

function isInteractiveCardTarget(target: EventTarget | null, cardRoot: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const interactive = target.closest(
    'button, a, input, textarea, select, [role="button"], [role="menuitem"], .MuiPopover-root, .MuiMenu-root, .MuiModal-root'
  );
  return Boolean(interactive && interactive !== cardRoot);
}

const cardShellSx = (theme: Theme) => ({
  position: 'relative' as const,
  overflow: 'hidden',
  height: '100%',
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: alpha(theme.palette.background.paper, 0.72),
  backgroundImage: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 52%)`,
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.45),
    transform: 'translateY(-2px)',
    boxShadow: `0 14px 36px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.28 : 0.1)}`,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

const HostStudentRosterCard: React.FC<HostStudentRosterCardProps> = ({
  student,
  onEdit,
  onReport,
  onUnblock,
  onRemove,
  canReport,
  canUnblock,
}) => {
  const { t, i18n } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const accountName = student.name?.trim() || null;
  const schoolName = schoolProfileLabel(student);
  const displayName =
    schoolName || accountName || student.phoneNumber || t('hostUsers.unnamedUser', 'Unnamed user');
  const showAccountAlias = Boolean(schoolName && accountName && schoolName !== accountName);
  const lessonCount = student.tutorBookingCount ?? 0;
  const profile = student.studentProfile;
  const showMenu = true;

  let lastBookedLabel: string | null = null;
  if (student.latestBookingAt) {
    try {
      lastBookedLabel = new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(student.latestBookingAt));
    } catch {
      lastBookedLabel = student.latestBookingAt;
    }
  }

  const chips: React.ReactNode[] = [];
  if (profile.adult === false) {
    chips.push(
      <Chip
        key="minor"
        size="small"
        color={profile.guardianContactRequired && !profile.guardianContact?.trim() ? 'warning' : 'default'}
        label={
          profile.guardianContactRequired && !profile.guardianContact?.trim()
            ? t('hostStudents.profile.minorMissingGuardian', 'Under 18 · guardian needed')
            : t('hostStudents.profile.minor', 'Under 18')
        }
        sx={{ fontWeight: 700, height: 24 }}
      />
    );
  } else if (profile.adult === true) {
    chips.push(
      <Chip
        key="adult"
        size="small"
        variant="outlined"
        label={t('hostStudents.profile.adult', 'Adult')}
        sx={{ fontWeight: 700, height: 24 }}
      />
    );
  } else if (!profile.firstName && !profile.lastName && !profile.dateOfBirth) {
    chips.push(
      <Chip
        key="incomplete"
        size="small"
        variant="outlined"
        label={t('hostStudents.profile.incomplete', 'Profile incomplete')}
        sx={{ fontWeight: 700, height: 24, color: 'text.secondary' }}
      />
    );
  }

  return (
    <Box
      sx={cardShellSx}
      onClick={(event) => {
        if (isInteractiveCardTarget(event.target, event.currentTarget)) return;
        onEdit(student);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (isInteractiveCardTarget(event.target, event.currentTarget)) return;
        event.preventDefault();
        onEdit(student);
      }}
      role="button"
      tabIndex={0}
    >
      <Stack spacing={1.75} sx={{ p: 2.25, height: '100%' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            src={student.profileImageUrl || undefined}
            alt=""
            variant="rounded"
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22),
              color: 'primary.light',
              fontWeight: 700,
              fontSize: '1.1rem',
              border: 1,
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
            }}
          >
            {tutorAvatarInitials(displayName)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  noWrap
                  sx={{ lineHeight: 1.25, letterSpacing: '-0.01em' }}
                >
                  {displayName}
                </Typography>
                {showAccountAlias && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {accountName}
                  </Typography>
                )}
              </Box>
              {showMenu && (
                <IconButton
                  size="small"
                  aria-label={t('common.more', 'More')}
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuAnchor(event.currentTarget);
                  }}
                  sx={{ mt: -0.5, mr: -0.75 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            {student.phoneNumber && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {student.phoneNumber}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>

        {chips.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {chips}
          </Stack>
        )}

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 'auto', pt: 0.25, color: 'text.secondary' }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EventAvailableRoundedIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption" fontWeight={600}>
              {t('hostStudents.bookingsCount', '{{count}} lesson', { count: lessonCount })}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            ·
          </Typography>
          <Typography variant="caption" noWrap>
            {lastBookedLabel
              ? t('hostStudents.lastBooked', 'Last booked {{date}}', { date: lastBookedLabel })
              : t('hostStudents.noLessonsYet', 'No lessons yet')}
          </Typography>
        </Stack>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(event) => event.stopPropagation()}
      >
        {canReport && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onReport(student);
            }}
          >
            <ListItemIcon>
              <FlagOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostUsers.reportUser', 'Report user')}</ListItemText>
          </MenuItem>
        )}
        {canUnblock && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onUnblock(student);
            }}
          >
            <ListItemIcon>
              <PersonOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostUsers.removeBlacklist', 'Remove blacklist')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onRemove(student);
          }}
        >
          <ListItemIcon>
            <PersonRemoveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('hostStudents.remove.action', 'Remove from school')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default HostStudentRosterCard;
